package com.arena_web.utils

import kotlinx.browser.document
import kotlinx.coroutines.suspendCancellableCoroutine
import kotlinx.coroutines.withTimeoutOrNull
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.HTMLVideoElement
import org.w3c.dom.url.URL
import org.w3c.files.Blob
import kotlin.math.floor
import kotlin.math.roundToInt

data class VideoPreviewInfo(
    /** Object URL of a JPEG poster frame, or null when the browser can't
     *  decode the video (unsupported codec). Caller revokes when done. */
    val posterUrl: String?,
    val durationSeconds: Double?,
)

/**
 * Captures a poster frame + duration from a (blob) video URL using a
 * detached `<video>` + canvas. `<video src=blob:...>` previews are flaky on
 * mobile (no frame paints until user interaction, fragments like `#t=0.1`
 * are ignored on blobs), so we decode one frame ourselves and hand back a
 * plain image the UI can render as a normal background.
 */
suspend fun captureVideoPreview(srcUrl: String): VideoPreviewInfo {
    return try {
        val video = document.createElement("video") as HTMLVideoElement
        video.muted = true
        video.setAttribute("playsinline", "")
        video.preload = "metadata"
        video.src = srcUrl

        val metaOk = withTimeoutOrNull(5000) {
            suspendCancellableCoroutine<Boolean> { cont ->
                video.addEventListener("loadedmetadata", { cont.resume(true) { _, _, _ -> } })
                video.addEventListener("error", { cont.resume(false) { _, _, _ -> } })
            }
        } ?: false
        if (!metaOk) return VideoPreviewInfo(null, null)

        val duration = video.duration.takeIf { it.isFinite() && it > 0 }

        // Seek a hair in — frame 0 is often black.
        val seekOk = withTimeoutOrNull(3000) {
            suspendCancellableCoroutine<Boolean> { cont ->
                video.addEventListener("seeked", { cont.resume(true) { _, _, _ -> } })
                video.addEventListener("error", { cont.resume(false) { _, _, _ -> } })
                video.currentTime = 0.1
            }
        } ?: false
        if (!seekOk || video.videoWidth <= 0) return VideoPreviewInfo(null, duration)

        val scale = (400.0 / video.videoWidth).coerceAtMost(1.0)
        val canvas = document.createElement("canvas") as HTMLCanvasElement
        canvas.width = (video.videoWidth * scale).roundToInt().coerceAtLeast(1)
        canvas.height = (video.videoHeight * scale).roundToInt().coerceAtLeast(1)
        canvas.asDynamic().getContext("2d")
            .drawImage(video, 0, 0, canvas.width, canvas.height)

        val blob = suspendCancellableCoroutine<Blob?> { cont ->
            canvas.asDynamic().toBlob({ b: Blob? -> cont.resume(b) { _, _, _ -> } }, "image/jpeg", 0.7)
        }
        VideoPreviewInfo(
            posterUrl = blob?.let { URL.createObjectURL(it) },
            durationSeconds = duration,
        )
    } catch (e: Throwable) {
        VideoPreviewInfo(null, null)
    }
}

/** `m:ss` label for a duration in seconds, or null when unknown. */
fun formatDurationSeconds(seconds: Double?): String? {
    if (seconds == null || !seconds.isFinite() || seconds <= 0) return null
    val total = floor(seconds).toInt()
    val m = total / 60
    val s = total % 60
    return "$m:${s.toString().padStart(2, '0')}"
}
